import React from "react";

import { Spinner } from "../ui";

export type SmeChatSuggestionPanelProps = {
  suggestions: string[];
  /** Suggestions already copied into the composer (matched by text). */
  usedSuggestions?: string[];
  loading?: boolean;
  waitingForClient?: boolean;
  disabled?: boolean;
  /** Current composer text, used to flag the suggestion that is "In composer". */
  composerText?: string;
  onUse: (question: string) => void;
};

export function SmeChatSuggestionPanel({
  suggestions,
  usedSuggestions = [],
  loading = false,
  waitingForClient = false,
  disabled = false,
  composerText = "",
  onUse,
}: SmeChatSuggestionPanelProps) {
  const used = new Set(usedSuggestions.map((q) => q.trim()));
  const inComposer = composerText.trim();
  const openCount = suggestions.filter((q) => !used.has(q.trim())).length;

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="smechat-aside__state" aria-live="polite">
        <Spinner size="sm" />
        <span>Generating suggestions…</span>
      </div>
    );
  } else if (waitingForClient) {
    body = (
      <p className="smechat-aside__state" role="status">
        Suggestions appear after the client replies.
      </p>
    );
  } else if (suggestions.length === 0) {
    body = <p className="smechat-aside__state">No suggestions right now.</p>;
  } else {
    body = (
      <ul className="smechat-suggest-list" aria-live="polite">
        {suggestions.map((question, index) => {
          const text = question.trim();
          const isInComposer = inComposer !== "" && inComposer === text;
          const isUsed = used.has(text);
          return (
            <li
              key={`${index}-${text.slice(0, 24)}`}
              className={`smechat-suggest${isUsed ? " is-used" : ""}${isInComposer ? " is-active" : ""}`}
            >
              <div className="smechat-suggest__meta">
                <span className="smechat-suggest__tag">Suggestion {index + 1}</span>
                {isInComposer ? (
                  <span className="smechat-suggest__status">In composer</span>
                ) : isUsed ? (
                  <span className="smechat-suggest__status">Used</span>
                ) : null}
              </div>
              <p className="smechat-suggest__text">{question}</p>
              {!isUsed ? (
                <div className="smechat-suggest__actions">
                  <button
                    type="button"
                    className="smechat-suggest__use"
                    disabled={disabled}
                    onClick={() => onUse(question)}
                  >
                    Use
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <aside className="smechat-aside" aria-label="Suggested questions">
      <header className="smechat-aside__head">
        <span className="smechat-aside__icon" aria-hidden="true">
          ✦
        </span>
        <div className="smechat-aside__titles">
          <h3 className="smechat-aside__title">Ask next</h3>
          <p className="smechat-aside__sub">
            AI suggestions based on the conversation so far
            {!loading && !waitingForClient && suggestions.length > 0
              ? ` · ${openCount} open`
              : ""}
          </p>
        </div>
      </header>
      <div className="smechat-aside__body">{body}</div>
    </aside>
  );
}

export default SmeChatSuggestionPanel;
